import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

interface ContentItem {
  id: string;
  key: string;
  title: string;
  content: string;
  content_type: 'text' | 'html' | 'markdown';
  category: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useContent = (key?: string, category?: string) => {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      let query = supabase.from('site_content').select('*');

      if (key) {
        query = query.eq('key', key);
      }

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error: fetchError } = await query.order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setContent(data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching content:', err);
      setError(err.message);
      setContent([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [key, category]);

  const getContent = (contentKey: string): string => {
    const item = content.find(c => c.key === contentKey);
    return item?.content || '';
  };

  const updateContent = async (contentKey: string, newContent: string) => {
    try {
      const { error: updateError } = await supabase
        .from('site_content')
        .update({ 
          content: newContent,
          updated_at: new Date().toISOString()
        })
        .eq('key', contentKey);

      if (updateError) throw updateError;

      // Update local state
      setContent(prev => prev.map(item => 
        item.key === contentKey 
          ? { ...item, content: newContent, updated_at: new Date().toISOString() }
          : item
      ));

      return true;
    } catch (err: any) {
      console.error('Error updating content:', err);
      setError(err.message);
      return false;
    }
  };

  return {
    content,
    loading,
    error,
    getContent,
    updateContent,
    refetch: fetchContent
  };
};

// Hook for getting a single content item
export const useContentItem = (key: string) => {
  const { content, loading, error, getContent, updateContent } = useContent(key);
  
  return {
    content: getContent(key),
    loading,
    error,
    updateContent: (newContent: string) => updateContent(key, newContent)
  };
};